import { ButtonV2 } from '@cimplico/components';
import {
    ArchiveBoxIcon,
    ArrowLeftOnRectangleIcon,
    ArrowUturnDownIcon,
    CloudArrowDownIcon,
    CloudArrowUpIcon,
    DocumentDuplicateIcon,
} from '@heroicons/react/20/solid';
import { useRef, useState } from 'react';
import { CustomWorksheetTemplate } from '../../types/custom-worksheet-v2';
import SecondaryPageActions from '../../components/secondary-page-actions/SecondaryPageActions';
import CWTArchive from './CWTArchive';
import CWTDuplicate from './CWTDuplicate';
import { toastNotification } from '../../components/toast/Toast';
import FileInput from '../files/FileInput';
import { useUpdateCWTSchema } from '../../queries/use-update-cwt-schema';
import { useUpdateCalculationTemplate } from '../../queries/resources/calculation-templates';

type CWTSecondaryActionsProps = {
    template: CustomWorksheetTemplate;
};

function CWTSecondaryActions({ template }: CWTSecondaryActionsProps): JSX.Element {
    const [action, setAction] = useState<string>();
    const importRef = useRef<HTMLInputElement | null>(null);
    const { mutate, mutateAsync } = useUpdateCalculationTemplate(template.id);
    const updateSchemaQuery = useUpdateCWTSchema(template.id);

    function clearAction(): void {
        setAction(undefined);
    }

    function restore(): void {
        mutate(
            { status: 'UNPUBLISHED' },
            {
                onSuccess: () => {
                    toastNotification('success', 'Template restored');
                },
                onError: () => {
                    toastNotification('error', 'Unable to restore template');
                },
            }
        );
    }

    function handleUnpublish(): void {
        mutate(
            { status: 'UNPUBLISHED' },
            {
                onSuccess: () => {
                    toastNotification('success', 'Template unpublished');
                },
                onError: () => {
                    toastNotification('error', 'Unable to unpublish template');
                },
            }
        );
    }

    /**
     * Downloads the template to be imported at a later date.
     *
     * An a tag is temporarily added to the dom and "clicked". The href is set to be the data of the template.
     */
    function download(): void {
        const data = {
            name: template.name,
            description: template.description,
            tags: template.tags,
            schema: template.schema,
            entityTypes: template.entityTypes,
            behaviour: template.behaviour,
            allowAsSchedule: template.allowAsSchedule,
        };

        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });

        const a = document.createElement('a');
        a.setAttribute('download', `${template.name}.cw`);
        a.setAttribute('href', URL.createObjectURL(blob));
        a.click();
        a.remove();
    }

    async function handleImport(files: File[]): Promise<void> {
        const file = files[0];

        if (!file) {
            return;
        }

        const contents = await file.text();
        const { name, description, tags, schema, entityTypes, behaviour, allowAsSchedule, accountBehaviour, type } = JSON.parse(contents);

        // The below checks are to support an older import version
        const _behaviour = behaviour !== undefined ? behaviour : accountBehaviour ?? undefined;
        const _allowAsSchedule = allowAsSchedule !== undefined ? allowAsSchedule : type ? type === 'CUSTOM_WORKSHEET' : undefined;

        await mutateAsync({
            name: name !== template.name ? name : undefined,
            description,
            tags,
            entityTypes,
            behaviour: _behaviour,
            allowAsSchedule: _allowAsSchedule,
        });
        await updateSchemaQuery.mutateAsync({ schema });
    }

    return (
        <>
            <FileInput onSelect={handleImport} innerRef={importRef} fileTypes={['.cw']} multiple={false} />
            {action === 'DUPLICATE' && (
                <CWTDuplicate
                    template={template}
                    onClose={clearAction}
                    dialogTitle="Duplicate"
                    dialogDescription="Duplicating this template will create a new unpublished copy. You will need to publish this copy before users have access to it."
                    successMessage="Successfully duplicated template"
                    primaryAction="Duplicate"
                />
            )}
            {action === 'ARCHIVE' && <CWTArchive templateId={template.id} onClose={clearAction} />}
            <SecondaryPageActions>
                {/* DUPLICATE */}
                <ButtonV2
                    size="sm"
                    label="Duplicate"
                    leftIcon={<DocumentDuplicateIcon />}
                    type="secondary"
                    onClick={(): void => setAction('DUPLICATE')}
                />
                {/* UN-PUBLISH */}
                {template.status === 'PUBLISHED' && (
                    <ButtonV2
                        size="sm"
                        label="Un-Publish"
                        leftIcon={<ArrowUturnDownIcon className="rotate-90" />}
                        type="secondary"
                        onClick={handleUnpublish}
                    />
                )}
                {/* DOWNLOAD */}
                <ButtonV2 size="sm" label="Download" leftIcon={<CloudArrowDownIcon />} type="secondary" onClick={download} />
                {/* IMPORT */}
                {template.status !== 'ARCHIVED' && (
                    <ButtonV2
                        size="sm"
                        label="Import"
                        leftIcon={<CloudArrowUpIcon />}
                        type="secondary"
                        onClick={(): void => {
                            importRef.current?.click();
                        }}
                    />
                )}
                {template.status === 'ARCHIVED' && (
                    <ButtonV2 size="sm" label="Unarchive" leftIcon={<ArrowLeftOnRectangleIcon />} type="secondary" onClick={restore} />
                )}

                {/* ARCHIVED */}
                {template.status !== 'ARCHIVED' && (
                    <ButtonV2
                        size="sm"
                        label="Archive"
                        leftIcon={<ArchiveBoxIcon className="text-red-600" />}
                        type="secondary"
                        onClick={(): void => setAction('ARCHIVE')}
                    />
                )}
            </SecondaryPageActions>
        </>
    );
}

export default CWTSecondaryActions;
